import React, { useState, useEffect, useRef } from 'react';
import './AdminDashboard.css';
import './AdminDashboard-Isolated.css';
import collegeConfig from '../../config/collegeConfig';
import ManageCourse from './ManageCourse';
import ManageTeachers from './ManageTeachers';
import ManageStudents from './ManageStudents';
import ManageAdmin from './ManageAdmin';
import ManageCollegeAdmin from './ManageCollegeAdmin';
import ManageNotice from './ManageNotice';
import ManageDRP from './ManageDRP';
import ManageDRPCUsers from './ManageDRPCUsers';
import PaymentSettings from './PaymentSettings';
import SystemSettings from './SystemSettings';
import AttendanceSettings from './AttendanceSettings';
import ManageDRPIncharge from './ManageDRPIncharge';
import ManageMonitorUsers from './ManageMonitorUsers';
import ManageDeanUsers from './ManageDeanUsers';
import ManageDepartmentMonitorUsers from './ManageDepartmentMonitorUsers';
import ManageLogbooks from './ManageLogbooks';
import ManageStudentAssignments from './ManageStudentAssignments';
import ManageAdditionalInfo from './ManageAdditionalInfo';
import ManageThesisFaculty from './ManageThesisFaculty';
import ManageBatchNames from './ManageBatchNames';
import ManageUGSubjects from './ManageUGSubjects';
import ManageUGBatches from './ManageUGBatches';

import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaBookMedical,
  FaCheckCircle,
  FaBell,
  FaUserShield,
  FaBook,
  FaChartBar,
  FaTachometerAlt,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaUser,
  FaHospital,
  FaUserMd,
  FaCreditCard,
  FaToggleOn,
  FaEye,
  FaTrashAlt,
  FaUserTie,
  FaUsersCog,
  FaInfoCircle,
  FaBuilding,
  FaGraduationCap,
  FaTags,
  FaCalendarAlt,
} from 'react-icons/fa';

const AdminDashboard = ({ onNavigate }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [user, setUser] = useState(null);
  
  // Real data state
  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalCourses: 0,
    activeStudents: 0,
    activeTeachers: 0,
    totalSubmissions: 0,
    verifiedSubmissions: 0
  });
  
  const [monthlySubmissions, setMonthlySubmissions] = useState([]);
  const [courseDistribution, setCourseDistribution] = useState([]);

  // UG program snapshot — mirrors dashboardStats/monthlySubmissions/courseDistribution above
  const [ugStats, setUgStats] = useState(null);
  const [ugTrend, setUgTrend] = useState([]);
  const [ugBatchDistribution, setUgBatchDistribution] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartsReady, setChartsReady] = useState(false);

  // Animated counter for stats
  const [animatedStats, setAnimatedStats] = useState({
    students: 0,
    teachers: 0,
    courses: 0,
    submitted: 0,
    verified: 0
  });

  // Close profile menu when clicking outside
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleOutsideClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [showProfileMenu]);

  // Get user data from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!token || !userData) {
      if (onNavigate) {
        onNavigate('login');
      }
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    } catch (error) {
      console.error('Error parsing user data:', error);
      if (onNavigate) {
        onNavigate('login');
      }
    }
  }, [onNavigate]);

  // Fetch dashboard statistics
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const token = localStorage.getItem('token');

        // Fetch all data in parallel for faster loading.
        // Logbook KPIs come from a server-side aggregation (admin-stats) instead of
        // fetching every logbook entry — pulling the whole collection here previously
        // crashed the server under production data volume.
        const [studentsResponse, teachersResponse, coursesResponse, logbookStatsResponse, ugOverviewResponse, ugTrendResponse, ugStudentsResponse] = await Promise.all([
          fetch(`${collegeConfig.api.baseURL}/students`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${collegeConfig.api.baseURL}/teachers`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${collegeConfig.api.baseURL}/courses`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${collegeConfig.api.baseURL}/logbook/admin-stats`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${collegeConfig.api.baseURL}/monitor/ug-overview`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${collegeConfig.api.baseURL}/monitor/ug-logbook-trend`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${collegeConfig.api.baseURL}/monitor/ug-all-students`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        let studentsData = [];
        let teachersData = [];
        let coursesData = [];
        let logbookStats = { monthlySubmissions: [], totalSubmissions: 0, verifiedSubmissions: 0 };

        if (studentsResponse.ok) {
          const data = await studentsResponse.json();
          studentsData = data.students || [];
        }

        if (teachersResponse.ok) {
          const data = await teachersResponse.json();
          teachersData = data.teachers || [];
        }

        if (coursesResponse.ok) {
          const data = await coursesResponse.json();
          coursesData = data.courses || [];
        }

        if (logbookStatsResponse.ok) {
          const data = await logbookStatsResponse.json();
          logbookStats = data;
        }

        if (ugOverviewResponse.ok) {
          const data = await ugOverviewResponse.json();
          if (data.success) setUgStats(data.overview);
        }

        if (ugTrendResponse.ok) {
          const data = await ugTrendResponse.json();
          if (data.success) {
            setUgTrend((data.trend || []).map(t => ({ month: t.month, submissions: t.submitted, verified: t.approved })));
          }
        }

        if (ugStudentsResponse.ok) {
          const data = await ugStudentsResponse.json();
          if (data.success) setUgBatchDistribution(calculateUGBatchDistribution(data.students || []));
        }

        setMonthlySubmissions(logbookStats.monthlySubmissions || []);

        // Calculate course distribution
        const distribution = calculateCourseDistribution(studentsData, coursesData);
        setCourseDistribution(distribution);

        // Calculate stats
        const stats = {
          totalStudents: studentsData.length,
          totalTeachers: teachersData.length,
          totalCourses: coursesData.length,
          activeStudents: studentsData.filter(s => s.isActive).length,
          activeTeachers: teachersData.filter(t => t.isActive).length,
          totalSubmissions: logbookStats.totalSubmissions || 0,
          verifiedSubmissions: logbookStats.verifiedSubmissions || 0
        };

        setDashboardStats(stats);
        
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        setError('Failed to load dashboard statistics');
      } finally {
        setLoading(false);
        // Set charts ready after a short delay to ensure data is fully set
        setTimeout(() => setChartsReady(true), 100);
      }
    };

    if (user) {
      fetchDashboardStats();
    }
  }, [user]);

  // Calculate course distribution
  const calculateCourseDistribution = (students, courses) => {
    const colors = ['#2ECC71', '#3498DB', '#F39C12', '#E74C3C', '#9B59B6', '#1ABC9C', '#E67E22', '#95A5A6'];
    
    const courseMap = new Map();
    
    students.forEach(student => {
      if (student.assignedCourses && student.assignedCourses.length > 0) {
        student.assignedCourses.forEach(ac => {
          const courseId = ac.courseId?._id || ac.courseId;
          if (courseId) {
            const count = courseMap.get(courseId) || 0;
            courseMap.set(courseId, count + 1);
          }
        });
      }
    });

    const distribution = [];
    let colorIndex = 0;

    courses.forEach(course => {
      const count = courseMap.get(course._id.toString()) || 0;
      if (count > 0) {
        distribution.push({
          category: course.name,
          value: count,
          color: colors[colorIndex % colors.length]
        });
        colorIndex++;
      }
    });

    return distribution;
  };

  // Calculate UG student distribution by batch — mirrors calculateCourseDistribution
  // above, just grouped by batchLabel (UG students have no assignedCourses).
  const calculateUGBatchDistribution = (ugStudents) => {
    const colors = ['#8e2c2c', '#F39C12', '#3498DB', '#1ABC9C', '#9B59B6', '#2ECC71', '#E67E22', '#95A5A6'];

    const batchMap = new Map();
    ugStudents.forEach(student => {
      const label = student.batchLabel || 'Unassigned';
      batchMap.set(label, (batchMap.get(label) || 0) + 1);
    });

    let colorIndex = 0;
    return Array.from(batchMap.entries()).map(([label, count]) => ({
      category: label,
      value: count,
      color: colors[colorIndex++ % colors.length]
    }));
  };

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animate numbers when dashboard stats change
  useEffect(() => {
    if (!loading && dashboardStats.totalStudents >= 0) {
      const targetStats = {
        students: dashboardStats.totalStudents,
        teachers: dashboardStats.totalTeachers,
        courses: dashboardStats.totalCourses,
        submitted: dashboardStats.totalSubmissions,
        verified: dashboardStats.verifiedSubmissions
      };

      const duration = 1500;
      const steps = 50;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        if (currentStep <= steps) {
          setAnimatedStats({
            students: Math.floor((targetStats.students * currentStep) / steps),
            teachers: Math.floor((targetStats.teachers * currentStep) / steps),
            courses: Math.floor((targetStats.courses * currentStep) / steps),
            submitted: Math.floor((targetStats.submitted * currentStep) / steps),
            verified: Math.floor((targetStats.verified * currentStep) / steps)
          });
        } else {
          clearInterval(timer);
          setAnimatedStats(targetStats);
        }
      }, stepDuration);

      return () => clearInterval(timer);
    }
  }, [dashboardStats, loading]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
  };

  const handleMenuClick = (menuId) => {
    setActiveMenu(menuId);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const sidebarMenuItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: <FaTachometerAlt />,
      color: '#2887f4ff'
    },
    {
      id: 'manage-admin',
      title: 'Manage Admin',
      icon: <FaUserShield />,
      color: '#b12ce6ff'
    },
    {
      id: 'manage-college-admin',
      title: 'Manage College Admin',
      icon: <FaUserShield />,
      color: '#8e44ad'
    },
    {
      id: 'manage-teachers',
      title: 'Manage Teachers',
      icon: <FaChalkboardTeacher />,
      color: '#E74C3C'
    },
    {
      id: 'manage-students',
      title: 'Manage Students',
      icon: <FaUserGraduate />,
      color: '#4A90E2'
    },
    {
      id: 'manage-student-assignments',
      title: 'Student Assignments',
      icon: <FaUsersCog />,
      color: '#2c8e6eff'
    },
    {
      id: 'manage-thesis-faculty',
      title: 'Thesis Faculty',
      icon: <FaGraduationCap />,
      color: '#7c3aed'
    },
    {
      id: 'manage-batch-names',
      title: 'Batch Names',
      icon: <FaTags />,
      color: '#0d9488'
    },
    {
      id: 'manage-drpc-users',
      title: 'Manage DRPC Users',
      icon: <FaUserMd />,
      color: '#9dff00ff'
    },
    {
      id: 'manage-drp',
      title: 'Manage DRP',
      icon: <FaHospital />,
      color: '#E67E22'
    },
    {
      id: 'manage-notice',
      title: 'Manage Notice',
      icon: <FaBell />,
      color: '#0099ffff'
    },
    {
      id: 'manage-additional-info',
      title: 'Additional Info',
      icon: <FaInfoCircle />,
      color: '#9B59B6'
    },
    {
      id: 'manage-course',
      title: 'Manage Course',
      icon: <FaBook />,
      color: '#27AE60'
    },
    {
      id: 'manage-ug-subjects',
      title: 'Manage UG Subjects',
      icon: <FaGraduationCap />,
      color: '#2E7D46'
    },
    {
      id: 'manage-ug-batches',
      title: 'Manage UG Batches',
      icon: <FaCalendarAlt />,
      color: '#2E7D46'
    },
    {
      id: 'payment-settings',
      title: 'Payment Settings',
      icon: <FaCreditCard />,
      color: '#1abc9c'
    },
    {
      id: 'system-settings',
      title: 'System Settings',
      icon: <FaToggleOn />,
      color: '#E67E22'
    },
    {
      id: 'attendance-settings',
      title: 'Attendance Integration',
      icon: <FaToggleOn />,
      color: '#6C3483'
    },
    {
      id: 'manage-drp-incharge',
      title: 'DRP Incharge Users',
      icon: <FaUserMd />,
      color: '#16a085'
    },
    {
      id: 'manage-monitor-users',
      title: 'Monitor Users',
      icon: <FaEye />,
      color: '#8e2c2c'
    },
    {
      id: 'manage-dean-users',
      title: 'Dean Users',
      icon: <FaUserTie />,
      color: '#1a5276'
    },
    {
      id: 'manage-department-monitor-users',
      title: 'Department Monitors',
      icon: <FaBuilding />,
      color: '#0f766e'
    },
    {
      id: 'manage-logbooks',
      title: 'Manage Logbooks',
      icon: <FaTrashAlt />,
      color: '#8e44ad'
    }
  ];

  const statsCards = [
    {
      title: 'Total Students',
      value: animatedStats.students,
      icon: <FaUserGraduate />,
      color: '#4A90E2',
    },
    {
      title: 'Total Teachers',
      value: animatedStats.teachers,
      icon: <FaChalkboardTeacher />,
      color: '#E74C3C',
    },
    {
      title: 'Total Courses',
      value: animatedStats.courses,
      icon: <FaBook />,
      color: '#27AE60',
    },
    {
      title: `E-Logbooks Submitted (${new Date().getFullYear()})`,
      value: animatedStats.submitted,
      icon: <FaBookMedical />,
      color: '#2ECC71',
    },
    {
      title: `Verified by Teachers (${new Date().getFullYear()})`,
      value: animatedStats.verified,
      icon: <FaCheckCircle />,
      color: '#F39C12',
    }
  ];

  // Chart components
  const SimpleLineChart = ({ data }) => {
    const maxValue = Math.max(...data.map(d => Math.max(d.submissions, d.verified)), 10);
    
    return (
      <div className="chart-container">
        <div className="chart-bars">
          {data.map((item, index) => (
            <div key={index} className="chart-bar-group">
              <div className="chart-bar-wrapper">
                <div 
                  className="chart-bar submitted" 
                  style={{ 
                    height: `${(item.submissions / maxValue) * 100}%`,
                    animation: chartsReady ? `growBar 1s ease-out ${index * 0.05}s forwards` : 'none'
                  }}
                >
                  <span className="bar-value">{item.submissions}</span>
                </div>
                <div 
                  className="chart-bar verified" 
                  style={{ 
                    height: `${(item.verified / maxValue) * 100}%`,
                    animation: chartsReady ? `growBar 1s ease-out ${index * 0.05}s forwards` : 'none'
                  }}
                >
                  <span className="bar-value">{item.verified}</span>
                </div>
              </div>
              <span className="chart-label">{item.month}</span>
            </div>
          ))}
        </div>
        <div className="chart-legend">
          <span><i className="legend-dot submitted"></i> Submitted</span>
          <span><i className="legend-dot verified"></i> Verified</span>
        </div>
      </div>
    );
  };

  const SimpleDonutChart = ({ data }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    if (total === 0) {
      return (
        <div className="donut-chart-container">
          <div className="no-data-message">
            <p>No course enrollment data available</p>
          </div>
        </div>
      );
    }

    return (
      <div className="donut-chart-container">
        <div className="donut-chart">
          <svg viewBox="0 0 42 42" className="donut">
            {data.map((item, index) => {
              const percentage = (item.value / total) * 100;
              const offset = data.slice(0, index).reduce((sum, d) => {
                return sum + ((d.value / total) * 100);
              }, 0);
              return (
                <circle
                  key={index}
                  className={`donut-segment ${chartsReady ? 'animate' : ''}`}
                  cx="21"
                  cy="21"
                  r="15.915"
                  fill="transparent"
                  stroke={item.color}
                  strokeWidth="3"
                  strokeDasharray={`${percentage} ${100 - percentage}`}
                  strokeDashoffset={-offset}
                  style={{ 
                    animationDelay: `${index * 0.2}s`
                  }}
                />
              );
            })}
          </svg>
          <div className="donut-center">
            <span className="center-value">{total}</span>
            <span className="center-label">Students</span>
          </div>
        </div>
        <div className="donut-legend">
          {data.map((item, index) => (
            <div key={index} className="legend-item">
              <span className="legend-color" style={{ background: item.color }}></span>
              <span className="legend-text">{item.category}: {item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        return (
          <>
            {/* Welcome Section */}
            <div className="welcome-section">
              <h1 className="welcome-title">Welcome back, {user?.name || 'Admin'}!</h1>
              <p className="welcome-subtitle">Monitor and manage your institution's e-learning platform</p>
            </div>

            {/* Loading or Error State */}
            {loading && (
              <div className="dashboard-loading">
                <div className="spinner"></div>
                <p>Loading dashboard statistics...</p>
              </div>
            )}
            
            {error && (
              <div className="alert alert-error">{error}</div>
            )}

            {/* Stats Cards */}
            {!loading && (
              <div className="stats-grid">
                {statsCards.map((stat, index) => (
                  <div key={index} className="stat-card" style={{ '--card-color': stat.color }}>
                    <div className="stat-icon1" style={{ background: stat.color }}>
                      {stat.icon}
                    </div>
                    <div className="stat-content">
                      <h3 className="stat-value">{stat.value.toLocaleString()}</h3>
                      <p className="stat-title">{stat.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Analytics Section */}
            {!loading && (
              <div className="analytics-section">
                <div className="analytics-card">
                  <h3 className="analytics-title">
                    <FaChartBar /> E-Logbook Submissions Trend ({new Date().getFullYear()})
                  </h3>
                  <SimpleLineChart data={monthlySubmissions} />
                </div>

                <div className="analytics-card">
                  <h3 className="analytics-title">
                    <FaUserGraduate /> Student Course Distribution
                  </h3>
                  <SimpleDonutChart data={courseDistribution} />
                </div>
              </div>
            )}

            {/* UG Program Snapshot */}
            {!loading && ugStats && (
              <>
                <div className="welcome-section" style={{ marginTop: '10px' }}>
                  <h1 className="welcome-title" style={{ fontSize: '1.4rem' }}>UG Program Snapshot</h1>
                  <p className="welcome-subtitle">Undergraduate (MBBS) competency logging activity</p>
                </div>

                <div className="stats-grid">
                  {[
                    { title: 'UG Students', value: ugStats.totalUGStudents, icon: <FaUserGraduate />, color: '#8e2c2c' },
                    { title: 'UG Faculty', value: ugStats.totalUGTeachers, icon: <FaChalkboardTeacher />, color: '#9B59B6' },
                    { title: 'UG Subjects', value: ugStats.totalUGSubjects, icon: <FaGraduationCap />, color: '#27AE60' },
                    { title: 'Competency Logs', value: ugStats.totalCompetencyLogs, icon: <FaBookMedical />, color: '#2ECC71' },
                    { title: 'Approval Rate', value: ugStats.approvalRate, icon: <FaCheckCircle />, color: '#F39C12' },
                  ].map((stat, index) => (
                    <div key={index} className="stat-card" style={{ '--card-color': stat.color }}>
                      <div className="stat-icon1" style={{ background: stat.color }}>
                        {stat.icon}
                      </div>
                      <div className="stat-content">
                        <h3 className="stat-value">{stat.title === 'Approval Rate' ? `${stat.value}%` : stat.value.toLocaleString()}</h3>
                        <p className="stat-title">{stat.title}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="analytics-section">
                  <div className="analytics-card">
                    <h3 className="analytics-title">
                      <FaChartBar /> UG Competency Log Trend ({new Date().getFullYear()})
                    </h3>
                    <SimpleLineChart data={ugTrend} />
                  </div>

                  <div className="analytics-card">
                    <h3 className="analytics-title">
                      <FaGraduationCap /> UG Student Distribution by Batch
                    </h3>
                    <SimpleDonutChart data={ugBatchDistribution} />
                  </div>
                </div>
              </>
            )}
          </>
        );
      
      case 'manage-admin':
        return <ManageAdmin />;

      case 'manage-college-admin':
        return <ManageCollegeAdmin />;

      case 'manage-teachers':
        return <ManageTeachers />;
      
      case 'manage-students':
        return <ManageStudents />;

      case 'manage-student-assignments':
        return <ManageStudentAssignments />;

      case 'manage-thesis-faculty':
        return <ManageThesisFaculty />;

      case 'manage-batch-names':
        return <ManageBatchNames />;

      case 'manage-drpc-users':
        return <ManageDRPCUsers />;
      
      case 'manage-drp':
        return <ManageDRP />;
      
      case 'manage-notice':
        return <ManageNotice />;

      case 'manage-additional-info':
        return <ManageAdditionalInfo />;

      case 'manage-course':
        return <ManageCourse />;

      case 'manage-ug-subjects':
        return <ManageUGSubjects />;

      case 'manage-ug-batches':
        return <ManageUGBatches />;

      case 'payment-settings':
        return <PaymentSettings />;

      case 'system-settings':
        return <SystemSettings />;

      case 'attendance-settings':
        return <AttendanceSettings />;

      case 'manage-drp-incharge':
        return <ManageDRPIncharge />;

      case 'manage-monitor-users':
        return <ManageMonitorUsers />;

      case 'manage-dean-users':
        return <ManageDeanUsers />;

      case 'manage-department-monitor-users':
        return <ManageDepartmentMonitorUsers />;

      case 'manage-logbooks':
        return <ManageLogbooks />;

      default:
        return null;
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="admin-dashboard-container">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img 
              src={collegeConfig.logo} 
              alt={`${collegeConfig.shortName} Logo`} 
              className="sidebar-logo" 
            />
            <div className="logo-text">
              <h2>{collegeConfig.shortName}</h2>
              <p>{collegeConfig.appName}</p>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {sidebarMenuItems.map((item) => (
            <div
              key={item.id}
              className={`sidebar-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.id)}
            >
              <span className="sidebar-icon" style={{ color: item.color }}>
                {item.icon}
              </span>
              <span className="sidebar-text">{item.title}</span>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-item logout-item" onClick={handleLogout}>
            <span className="sidebar-icon">
              <FaSignOutAlt />
            </span>
            <span className="sidebar-text">Logout</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Header */}
        <header className="admin-header">
          <div className="header-left">
            <button className="sidebar-toggle" onClick={toggleSidebar}>
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
            <h2 className="page-title">
              {sidebarMenuItems.find(item => item.id === activeMenu)?.title || 'Dashboard'}
            </h2>
          </div>

          <div className="header-right">
            <div className="profile-section" onClick={() => setShowProfileMenu(!showProfileMenu)}>
              <div className="profile-avatar">
                <FaUser />
              </div>
              <div className="profile-info">
                <span className="profile-name">{user?.name || 'Admin'}</span>
                <span className="profile-role">Administrator</span>
              </div>
              <span className="dropdown-arrow">▼</span>
              
              {showProfileMenu && (
                <div className="profile-dropdown">
                  <div className="dropdown-item logout" onClick={handleLogout}>
                    <span className="dropdown-icon">🚪</span>
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="page-content">
          {renderContent()}
        </div>

        {/* Footer */}
        <footer className="admin-footer">
          <p>© {collegeConfig.copyrightYear} {collegeConfig.collegeName} | Admin Dashboard v{collegeConfig.version}</p>
        </footer>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>
      )}
    </div>
  );
};

export default AdminDashboard;